import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Archive,
    ArchiveRestore,
    Bot,
    Eye,
    Loader2,
    MoreHorizontal,
    Plus,
    RefreshCw,
    Search,
    Sparkles,
    Trash2,
    Users,
    type LucideIcon,
} from 'lucide-react';
import { RainbowBeamsBackdrop } from '@/components/RainbowBeamsBackdrop';
import { SidebarExpandButton } from '@/components/SidebarExpandButton';
import {
    agentApi,
    type SearchV3ListItem,
    type SearchV3ListState,
    type SearchV3ListSummary,
} from '@/lib/agent.api';
import { getApiErrorMessage } from '@/lib/http';

const C = {
    ink: '#1F2428',
    muted: '#66717C',
    faint: '#98A1AA',
    line: '#E2E7EC',
    glassTop: 'rgba(255, 255, 255, 0.46)',
    glassRail: 'rgba(255, 255, 255, 0.64)',
    glassCard: 'rgba(255, 255, 255, 0.76)',
    glassPanel: 'rgba(255, 255, 255, 0.84)',
    glassTableHead: 'rgba(255, 255, 255, 0.68)',
    glassRow: 'rgba(255, 255, 255, 0.82)',
    glassRowHover: 'rgba(248, 250, 252, 0.92)',
    glassControl: 'rgba(255, 255, 255, 0.72)',
    lineGlass: 'rgba(153, 165, 178, 0.22)',
    coral: '#FD775E',
    coralSoft: '#FFF0EC',
    blue: '#2F6FBA',
    blueSoft: '#EAF2FB',
    green: '#1E7F5F',
    greenSoft: '#EAF7F0',
    amber: '#A56517',
    amberSoft: '#FFF4DF',
    red: '#B83D2F',
    redSoft: '#FFF0EE',
    gray: '#69727C',
    graySoft: '#F0EFEB',
    purple: '#7654B8',
    purpleSoft: '#F1ECFB',
} as const;

const emptySummary: SearchV3ListSummary = {
    activeAgents: 0,
    pendingReview: 0,
    newToday: 0,
    sourcedCandidates: 0,
};
const SEARCH_V3_LIST_POLL_INTERVAL_MS = 15000;

function fmt(n: number) {
    if (!Number.isFinite(n)) return '0';
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
    if (n >= 10_000) return `${Math.round(n / 1_000)}K`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return n.toLocaleString();
}

function formatHour(hour?: number | null) {
    if (!Number.isFinite(Number(hour))) return '9:00 AM';
    return new Date(Date.UTC(2026, 0, 1, Number(hour), 0, 0, 0)).toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
        timeZone: 'UTC',
    });
}

function searchDetailPath(item: SearchV3ListItem) {
    if (item.pendingReviewCount <= 0) return `/search-v3/${item.chatId}`;
    const params = new URLSearchParams({
        artifactTab: 'sourced',
        review: 'pending',
        artifactWidth: 'max',
    });
    return `/search-v3/${item.chatId}?${params.toString()}`;
}

function stateMeta(state: SearchV3ListState) {
    switch (state) {
        case 'daily_active':
            return { label: 'Daily Active', fg: C.green, bg: C.greenSoft };
        case 'attention_needed':
            return { label: 'Attention Needed', fg: C.red, bg: C.redSoft };
        case 'calibrating':
            return { label: 'Calibrating', fg: C.amber, bg: C.amberSoft };
        case 'paused':
            return { label: 'Paused', fg: C.gray, bg: C.graySoft };
        case 'archived':
            return { label: 'Archived', fg: C.purple, bg: C.purpleSoft };
        case 'idle':
        default:
            return { label: 'Idle', fg: C.blue, bg: C.blueSoft };
    }
}

function dayLabel(index: number) {
    const ago = 6 - index;
    if (ago === 0) return 'Today';
    if (ago === 1) return 'Yesterday';
    return `${ago}d ago`;
}

export function SearchV3ListPage() {
    const navigate = useNavigate();
    const [items, setItems] = useState<SearchV3ListItem[]>([]);
    const [summary, setSummary] = useState<SearchV3ListSummary>(emptySummary);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [openMenuId, setOpenMenuId] = useState<string | null>(null);
    const [busyId, setBusyId] = useState<string | null>(null);
    const loadInFlightRef = useRef(false);
    const pollTimerRef = useRef<number | null>(null);

    const load = useCallback(async ({ silent = false }: { silent?: boolean } = {}) => {
        if (loadInFlightRef.current) {
            return;
        }
        loadInFlightRef.current = true;
        if (!silent) {
            setLoading(true);
            setError(null);
        }
        try {
            const data = await agentApi.listSearchV3Summaries({
                includeArchived: true,
                limit: 150,
            });
            setItems(data.results);
            setSummary(data.summary);
        } catch (err) {
            if (!silent) {
                setError(getApiErrorMessage(err, 'Could not load searches.'));
            }
        } finally {
            setLoading(false);
            loadInFlightRef.current = false;
        }
    }, []);

    useEffect(() => {
        void load();
    }, [load]);

    useEffect(() => {
        const clearPoll = () => {
            if (pollTimerRef.current != null) {
                window.clearTimeout(pollTimerRef.current);
                pollTimerRef.current = null;
            }
        };
        const schedule = () => {
            pollTimerRef.current = window.setTimeout(tick, SEARCH_V3_LIST_POLL_INTERVAL_MS);
        };
        const tick = async () => {
            if (document.visibilityState === 'visible') {
                await load({ silent: true });
            }
            schedule();
        };
        const refreshOnVisible = () => {
            if (document.visibilityState === 'visible') {
                void load({ silent: true });
            }
        };

        schedule();
        document.addEventListener('visibilitychange', refreshOnVisible);
        window.addEventListener('focus', refreshOnVisible);
        return () => {
            clearPoll();
            document.removeEventListener('visibilitychange', refreshOnVisible);
            window.removeEventListener('focus', refreshOnVisible);
        };
    }, [load]);

    const metrics = useMemo(
        () => [
            { icon: Bot, label: 'Active agents', value: String(summary.activeAgents), tone: 'blue' as const },
            { icon: Eye, label: 'Needs review', value: String(summary.pendingReview), tone: 'amber' as const },
            { icon: Sparkles, label: 'New today', value: String(summary.newToday), tone: 'green' as const },
            { icon: Users, label: 'Sourced candidates', value: String(summary.sourcedCandidates), tone: 'coral' as const },
        ],
        [summary],
    );

    async function runMenuAction(item: SearchV3ListItem, action: 'archive' | 'unarchive' | 'delete') {
        setBusyId(item.chatId);
        setOpenMenuId(null);
        setError(null);
        try {
            if (action === 'archive') await agentApi.archive(item.chatId);
            if (action === 'unarchive') await agentApi.unarchive(item.chatId);
            if (action === 'delete') {
                const message =
                    item.state === 'archived'
                        ? `Permanently delete "${item.title}"? This removes its ranked pool, sourced candidates, and history. This cannot be undone.`
                        : `Delete "${item.title}"?`;
                const ok = window.confirm(message);
                if (!ok) return;
                await agentApi.deleteIdleSearchV3(item.chatId);
            }
            await load();
        } catch (err) {
            setError(getApiErrorMessage(err, 'Could not update this search.'));
        } finally {
            setBusyId(null);
        }
    }

    return (
        <div className="search-v3-list">
            <style>{styles}</style>
            <RainbowBeamsBackdrop
                style={{ top: -360, width: '118%', opacity: 0.78 }}
                grainOpacity={0.32}
            />
            <header className="search-v3-list-top">
                <div className="search-v3-list-title">
                    <SidebarExpandButton style={{ marginLeft: -6 }} />
                    <span className="search-v3-list-mark">
                        <Search size={18} strokeWidth={1.8} />
                    </span>
                    <div>
                        <h1>Searches</h1>
                        <p>Ranked pools, criteria scoring, sourced candidates, and active search agents.</p>
                    </div>
                </div>
                <div className="search-v3-list-actions">
                    <button
                        className="search-v3-icon-btn"
                        type="button"
                        aria-label="Refresh searches"
                        title="Refresh searches"
                        onClick={() => {
                            setLoading(true);
                            void load();
                        }}
                    >
                        {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                    </button>
                    <GradientNewSearchButton onClick={() => navigate('/search-v3/new')} />
                </div>
            </header>

            <section className="search-v3-summary" aria-label="Search summary">
                {metrics.map((metric) => (
                    <SummaryMetric key={metric.label} {...metric} />
                ))}
            </section>

            <main className="search-v3-main">
                <section className="search-v3-panel">
                    {error ? (
                        <div className="search-v3-error">{error}</div>
                    ) : null}
                    <div className="search-v3-table" role="table" aria-label="Searches">
                        <div className="search-v3-table-head" role="row">
                            <span>Search</span>
                            <span>State</span>
                            <span>Auto-source</span>
                            <span>7-day sourced</span>
                            <span>Ranked pool</span>
                            <span>Sourced</span>
                            <span aria-label="Actions" />
                        </div>
                        {loading && items.length === 0 ? (
                            <div className="search-v3-empty">
                                <Loader2 size={16} className="animate-spin" />
                                Loading searches
                            </div>
                        ) : items.length === 0 ? (
                            <div className="search-v3-empty">
                                No searches yet.
                            </div>
                        ) : (
                            items.map((item) => (
                                <SearchRow
                                    key={item.chatId}
                                    item={item}
                                    busy={busyId === item.chatId}
                                    menuOpen={openMenuId === item.chatId}
                                    onOpen={() => navigate(searchDetailPath(item))}
                                    onMenuToggle={() =>
                                        setOpenMenuId((current) =>
                                            current === item.chatId ? null : item.chatId,
                                        )
                                    }
                                    onArchive={() => void runMenuAction(item, 'archive')}
                                    onUnarchive={() => void runMenuAction(item, 'unarchive')}
                                    onDelete={() => void runMenuAction(item, 'delete')}
                                />
                            ))
                        )}
                    </div>
                </section>
            </main>
        </div>
    );
}

function SummaryMetric({
    icon: Icon,
    label,
    value,
    tone,
}: {
    icon: LucideIcon;
    label: string;
    value: string;
    tone: 'blue' | 'amber' | 'green' | 'coral';
}) {
    return (
        <div className={`search-v3-summary-card is-${tone}`}>
            <span>
                <Icon size={16} strokeWidth={1.8} />
            </span>
            <div>
                <strong>{value}</strong>
                <small>{label}</small>
            </div>
        </div>
    );
}

function GradientNewSearchButton({ onClick }: { onClick: () => void }) {
    return (
        <div className="search-v3-gradient-action">
            <div className="search-v3-gradient-action-border">
                <div className="search-v3-gradient-action-fill" />
            </div>
            <button
                type="button"
                onClick={onClick}
                className="search-v3-gradient-action-button"
            >
                <Plus size={16} strokeWidth={2.2} />
                New search
            </button>
        </div>
    );
}

function SearchRow({
    item,
    busy,
    menuOpen,
    onOpen,
    onMenuToggle,
    onArchive,
    onUnarchive,
    onDelete,
}: {
    item: SearchV3ListItem;
    busy: boolean;
    menuOpen: boolean;
    onOpen: () => void;
    onMenuToggle: () => void;
    onArchive: () => void;
    onUnarchive: () => void;
    onDelete: () => void;
}) {
    const meta = stateMeta(item.state);
    const sourced7d = item.sourced7d.length === 7 ? item.sourced7d : Array(7).fill(0);
    const sevenDayTotal = sourced7d.reduce((sum, n) => sum + n, 0);
    const max = Math.max(...sourced7d, 1);
    const subtitle = item.description || item.roleLabel;
    return (
        <div
            className="search-v3-row"
            role="row"
            tabIndex={0}
            onClick={onOpen}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onOpen();
                }
            }}
        >
            <span className="search-v3-search-cell">
                <span>
                    <span className="search-v3-title-line">
                        <strong title={item.title}>{item.title}</strong>
                        {item.pendingReviewCount > 0 ? (
                            <span className="search-v3-review-chip">
                                {item.pendingReviewCount} review
                            </span>
                        ) : null}
                    </span>
                    <small title={subtitle}>{subtitle}</small>
                </span>
            </span>
            <span>
                <span className="search-v3-state-chip" style={{ color: meta.fg, background: meta.bg }}>
                    {meta.label}
                </span>
            </span>
            <span className="search-v3-auto-cell">
                <strong style={{ color: item.autoSource.enabled ? C.green : C.gray }}>
                    {item.autoSource.enabled ? 'Daily on' : 'Off'}
                </strong>
                {item.autoSource.enabled ? (
                    <small>
                        {formatHour(item.autoSource.scheduleHour)} · {item.autoSource.dailyTarget || 25}/day
                    </small>
                ) : (
                    <small>Daily cadence off</small>
                )}
            </span>
            <span className="search-v3-trend">
                <span className="search-v3-bars" aria-label={`7-day sourced: ${sevenDayTotal} total`}>
                    {sourced7d.map((value, index) => (
                        <span
                            key={index}
                            className="search-v3-bar"
                            data-tooltip={`${dayLabel(index)}: ${value} sourced`}
                            aria-label={`${dayLabel(index)}: ${value} sourced`}
                            style={{ height: `${Math.max(4, Math.round((value / max) * 28))}px` }}
                        />
                    ))}
                </span>
                <small>{sevenDayTotal} / 7d</small>
            </span>
            <span className="search-v3-stat-cell">
                <strong>{fmt(item.rankedPoolCount)}</strong>
            </span>
            <span className="search-v3-stat-cell">
                <strong>{fmt(item.sourcedCount)}</strong>
            </span>
            <span className="search-v3-menu-cell" onClick={(event) => event.stopPropagation()}>
                <button
                    className={`search-v3-menu-button ${menuOpen ? 'is-open' : ''}`}
                    type="button"
                    aria-haspopup="menu"
                    aria-expanded={menuOpen}
                    aria-label={`More actions for ${item.title}`}
                    onClick={onMenuToggle}
                    disabled={busy}
                >
                    {busy ? <Loader2 size={16} className="animate-spin" /> : <MoreHorizontal size={17} />}
                </button>
                {menuOpen ? (
                    <span className="search-v3-row-menu" role="menu">
                        {item.state === 'archived' ? (
                            <button type="button" role="menuitem" onClick={onUnarchive}>
                                <ArchiveRestore size={14} />
                                Unarchive
                            </button>
                        ) : (
                            <button type="button" role="menuitem" onClick={onArchive}>
                                <Archive size={14} />
                                Archive
                            </button>
                        )}
                        {item.canDelete ? (
                            <button className="is-danger" type="button" role="menuitem" onClick={onDelete}>
                                <Trash2 size={14} />
                                Delete
                            </button>
                        ) : null}
                    </span>
                ) : null}
            </span>
        </div>
    );
}

const styles = `
.search-v3-list {
    position: relative;
    isolation: isolate;
    overflow: hidden;
    min-height: 100%;
    padding: 28px 34px 48px;
    background: #FAFAFA;
    color: ${C.ink};
    font-family: var(--font-sans);
}
.search-v3-list-top,
.search-v3-summary,
.search-v3-panel {
    position: relative;
    z-index: 1;
    border: 1px solid ${C.lineGlass};
    border-radius: 8px;
    backdrop-filter: blur(18px);
    box-shadow: 0 18px 50px rgba(31, 36, 40, 0.05);
}
.search-v3-list-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    padding: 18px 20px;
    background: ${C.glassTop};
}
.search-v3-list-title {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 13px;
}
.search-v3-list-mark {
    display: inline-flex;
    width: 34px;
    height: 34px;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    background: rgba(255,255,255,0.72);
    color: ${C.coral};
    border: 1px solid ${C.lineGlass};
}
.search-v3-list h1 {
    margin: 0;
    font-family: var(--font-serif);
    font-size: 31px;
    font-weight: 520;
    letter-spacing: 0;
    line-height: 1.08;
}
.search-v3-list p {
    margin: 4px 0 0;
    color: ${C.muted};
    font-size: 13px;
    line-height: 1.45;
}
.search-v3-list-actions {
    display: flex;
    align-items: center;
    gap: 10px;
}
.search-v3-icon-btn,
.search-v3-primary-btn {
    display: inline-flex;
    height: 36px;
    align-items: center;
    justify-content: center;
    gap: 7px;
    border-radius: 8px;
    border: 1px solid ${C.lineGlass};
    background: ${C.glassControl};
    color: ${C.ink};
    cursor: pointer;
    font-weight: 650;
}
.search-v3-icon-btn { width: 36px; }
.search-v3-primary-btn {
    padding: 0 13px;
    background: ${C.coral};
    border-color: rgba(253,119,94,0.22);
    color: white;
}
.search-v3-gradient-action {
    position: relative;
    display: inline-flex;
    border-radius: 12px;
    filter: drop-shadow(0px 4px 9px rgba(255, 123, 41, 0.4));
}
.search-v3-gradient-action-border {
    position: absolute;
    inset: 0;
    padding: 1px;
    border-radius: 12px;
    background: linear-gradient(to bottom right, #EC87E0, #FFB000);
    pointer-events: none;
}
.search-v3-gradient-action-fill {
    height: 100%;
    width: 100%;
    border-radius: 11px;
    background: #FFFFFF;
}
.search-v3-gradient-action-button {
    position: relative;
    display: inline-flex;
    width: 126px;
    height: 36px;
    align-items: center;
    justify-content: center;
    gap: 5px;
    border: 0;
    border-radius: 12px;
    background: transparent;
    padding: 4px 13px;
    color: #000000;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
    cursor: pointer;
    font-family: Outfit, var(--font-sans), sans-serif;
    font-size: 14px;
    font-weight: 500;
    letter-spacing: 0;
    line-height: 20px;
    white-space: nowrap;
}
.search-v3-gradient-action-button:hover {
    background: #F9FAFB;
}
.search-v3-gradient-action-button:focus-visible {
    outline: 2px solid #4F46E5;
    outline-offset: 2px;
}
.search-v3-summary {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px;
    margin-top: 14px;
    padding: 10px;
    background: ${C.glassRail};
}
.search-v3-summary-card {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 10px;
    min-height: 70px;
    padding: 13px;
    border-radius: 8px;
    background: ${C.glassCard};
    border: 1px solid ${C.lineGlass};
}
.search-v3-summary-card > span {
    display: inline-flex;
    width: 31px;
    height: 31px;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
}
.search-v3-summary-card strong {
    display: block;
    font-size: 20px;
    line-height: 1;
}
.search-v3-summary-card small {
    display: block;
    margin-top: 4px;
    color: ${C.muted};
    font-size: 12px;
}
.search-v3-summary-card.is-blue > span { background: ${C.blueSoft}; color: ${C.blue}; }
.search-v3-summary-card.is-amber > span { background: ${C.amberSoft}; color: ${C.amber}; }
.search-v3-summary-card.is-green > span { background: ${C.greenSoft}; color: ${C.green}; }
.search-v3-summary-card.is-coral > span { background: ${C.coralSoft}; color: ${C.coral}; }
.search-v3-main {
    position: relative;
    z-index: 1;
    margin-top: 14px;
}
.search-v3-panel {
    overflow: visible;
    padding: 10px;
    background: ${C.glassPanel};
}
.search-v3-error {
    margin: 0 0 10px;
    border-radius: 8px;
    padding: 10px 12px;
    color: ${C.red};
    background: ${C.redSoft};
    font-size: 13px;
    font-weight: 600;
}
.search-v3-table {
    min-width: 1080px;
}
.search-v3-table-head,
.search-v3-row {
    display: grid;
    grid-template-columns: minmax(280px, 1.18fr) minmax(138px, 0.56fr) minmax(158px, 0.7fr) minmax(122px, 0.58fr) 96px 74px 42px;
    gap: 12px;
    align-items: center;
}
.search-v3-table-head {
    height: 38px;
    padding: 0 12px;
    border-radius: 8px;
    background: ${C.glassTableHead};
    color: ${C.faint};
    font-size: 11px;
    font-weight: 760;
    text-transform: uppercase;
}
.search-v3-row {
    position: relative;
    min-height: 76px;
    margin-top: 8px;
    padding: 12px;
    border: 1px solid ${C.lineGlass};
    border-radius: 8px;
    background: ${C.glassRow};
    cursor: pointer;
    transition: background 140ms ease, border-color 140ms ease, box-shadow 140ms ease;
}
.search-v3-row::before {
    content: '';
    position: absolute;
    left: 0;
    top: 10px;
    bottom: 10px;
    width: 2px;
    border-radius: 999px;
    background: transparent;
}
.search-v3-row:hover {
    background: ${C.glassRowHover};
    border-color: rgba(253,119,94,0.2);
    box-shadow: 0 14px 34px rgba(31, 36, 40, 0.05);
}
.search-v3-row:hover::before { background: ${C.coral}; }
.search-v3-search-cell,
.search-v3-auto-cell,
.search-v3-trend,
.search-v3-stat-cell {
    min-width: 0;
}
.search-v3-title-line {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 8px;
}
.search-v3-title-line strong {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 14px;
}
.search-v3-search-cell small,
.search-v3-auto-cell small,
.search-v3-trend small {
    display: block;
    margin-top: 4px;
    overflow: hidden;
    color: ${C.muted};
    font-size: 12px;
    line-height: 1.3;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.search-v3-review-chip {
    flex: 0 0 auto;
    border-radius: 999px;
    padding: 2px 7px;
    background: ${C.coralSoft};
    color: ${C.coral};
    font-size: 11px;
    font-weight: 760;
}
.search-v3-state-chip {
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    border-radius: 999px;
    padding: 4px 8px;
    font-size: 12px;
    font-weight: 760;
}
.search-v3-auto-cell strong,
.search-v3-stat-cell strong {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 13px;
}
.search-v3-bars {
    display: flex;
    height: 31px;
    align-items: flex-end;
    gap: 3px;
}
.search-v3-bar {
    position: relative;
    display: block;
    width: 8px;
    border-radius: 999px 999px 2px 2px;
    background: linear-gradient(180deg, rgba(30,127,95,0.8), rgba(30,127,95,0.28));
}
.search-v3-bar::after {
    content: attr(data-tooltip);
    position: absolute;
    left: 50%;
    bottom: calc(100% + 8px);
    z-index: 6;
    width: max-content;
    max-width: 160px;
    transform: translateX(-50%) translateY(3px);
    border-radius: 6px;
    padding: 5px 7px;
    background: rgba(31, 36, 40, 0.94);
    color: white;
    font-size: 11px;
    font-weight: 650;
    line-height: 1.2;
    opacity: 0;
    pointer-events: none;
    white-space: nowrap;
    box-shadow: 0 8px 20px rgba(31, 36, 40, 0.16);
    transition: opacity 120ms ease, transform 120ms ease;
}
.search-v3-bar::before {
    content: '';
    position: absolute;
    left: 50%;
    bottom: calc(100% + 3px);
    z-index: 6;
    transform: translateX(-50%) translateY(3px);
    border: 5px solid transparent;
    border-top-color: rgba(31, 36, 40, 0.94);
    opacity: 0;
    pointer-events: none;
    transition: opacity 120ms ease, transform 120ms ease;
}
.search-v3-bar:hover::after,
.search-v3-bar:hover::before {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
}
.search-v3-menu-cell {
    position: relative;
    display: flex;
    justify-content: flex-end;
}
.search-v3-menu-button {
    display: inline-flex;
    width: 30px;
    height: 30px;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    border: 1px solid transparent;
    background: transparent;
    color: ${C.muted};
    cursor: pointer;
}
.search-v3-menu-button:hover,
.search-v3-menu-button.is-open {
    background: rgba(255,255,255,0.9);
    border-color: ${C.lineGlass};
    color: ${C.ink};
}
.search-v3-row-menu {
    position: absolute;
    top: 34px;
    right: 0;
    z-index: 4;
    display: grid;
    min-width: 142px;
    gap: 3px;
    padding: 5px;
    border-radius: 8px;
    border: 1px solid ${C.line};
    background: white;
    box-shadow: 0 16px 38px rgba(31,36,40,0.14);
}
.search-v3-row-menu button {
    display: flex;
    align-items: center;
    gap: 8px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    padding: 8px;
    color: ${C.ink};
    cursor: pointer;
    font-size: 12px;
    font-weight: 650;
    text-align: left;
}
.search-v3-row-menu button:hover { background: #F7F8FA; }
.search-v3-row-menu button.is-danger { color: ${C.red}; }
.search-v3-empty {
    display: flex;
    min-height: 180px;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: ${C.muted};
    font-size: 13px;
}
@media (max-width: 1120px) {
    .search-v3-list { padding: 18px 14px 32px; }
    .search-v3-summary { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .search-v3-panel { overflow-x: auto; }
}
@media (max-width: 680px) {
    .search-v3-list-top { align-items: flex-start; flex-direction: column; }
    .search-v3-summary { grid-template-columns: 1fr; }
}
`;
